import React, { useCallback } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import type { MessageDescriptor } from 'react-intl';
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';

import Heading from '@atlaskit/heading/heading';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { xcss } from '@atlaskit/primitives/xcss';
import Stack from '@atlaskit/primitives/stack';
import Box from '@atlaskit/primitives/box';
import Text from '@atlaskit/primitives/text';

import { ObservableSpinner } from '@confluence/infinite-scroll';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import type { ContentTypeEventService } from '@confluence/content-types-bridge/entry-points/content-types-event-service.ts';
import { isCanvasContentType } from '@confluence/content-types-utils/entry-points/contentTypeConstants.ts';

import type { useVersionHistoryContentVersionHistoryQuery_contentVersionHistory_ContentVersionHistoryConnection_nodes_ContentVersion as VersionHistoryNodeType } from './__types__/useVersionHistoryContentVersionHistoryQuery.ts';
import { useVersionHistoryAnalytics } from './useVersionHistoryAnalytics.ts';
import { VersionHistoryItem, VersionHistoryCurrentVersionItem } from './VersionHistoryItem.tsx';
import { ErrorState } from './ErrorState.tsx';

const i18n = defineMessages({
	currentVersionTitle: {
		id: 'version-history.current.version.title',
		defaultMessage: 'Current version',
		description:
			'The text is shown as a section heading in the version history side panel above the item representing the most recently saved state of the content.',
	},
	autoSavedVersionsTitle: {
		id: 'version-history.auto-saved.version.title',
		defaultMessage: 'Autosaved versions',
		description:
			'The text is shown as a section heading in the version history side panel above the list of autosaved historical versions of the content.',
	},
	historyAvailabilityInfo: {
		id: 'version-history.history.availability.info',
		defaultMessage: 'Whiteboards history is available for the past 30 days.',
		description: 'text explaining that history is only available for the past 30 days',
	},
	slideHistoryAvailabilityInfo: {
		id: 'version-history.slide.history.availability.info',
		defaultMessage: 'Slides history is available for the past 30 days.',
		description: 'text explaining that slide history is only available for the past 30 days',
	},
	databasehHistoryAvailabilityInfo: {
		id: 'version-history.database.history.availability.info',
		defaultMessage: 'Databases history is available for the past 30 days.',
		description: 'text explaining that database history is only available for the past 30 days',
	},
});

type VersionHistoryContentTypeMapping = {
	[key: string]: MessageDescriptor;
};

const contentTypeMessaging: VersionHistoryContentTypeMapping = {
	whiteboard: i18n.historyAvailabilityInfo,
	slide: i18n.slideHistoryAvailabilityInfo,
	database: i18n.databasehHistoryAvailabilityInfo,
};

const bodyContentWrapperStyles = xcss({
	overflowY: 'auto',
	flexGrow: 1,
});

const SectionTitle = ({ title }: { title: MessageDescriptor }) => (
	<Box paddingBlock="space.100" paddingInlineStart="space.300" paddingInlineEnd="space.150">
		<Heading size="xsmall">
			<FormattedMessage {...title} />
		</Heading>
	</Box>
);

type VersionHistoryContentTypes = {
	error: ApolloErrorInterop | undefined;
	autosavedVersions: readonly VersionHistoryNodeType[];
	hasNextPage: boolean;
	loadMoreVersions: () => void;
	cursor: string | null;
	refetch: () => void;
	loading: boolean;
	handleVersionClick: (version: VersionHistoryNodeType | null) => void;
	selectedVersion: VersionHistoryNodeType | null;
	eventService: ContentTypeEventService;
	contentType: string;
	title: string;
};

export const VersionHistoryContent = ({
	error,
	autosavedVersions,
	hasNextPage,
	loadMoreVersions,
	cursor,
	refetch,
	loading,
	handleVersionClick,
	selectedVersion,
	eventService,
	contentType,
	title,
}: VersionHistoryContentTypes) => {
	const { onCopyVersionContent } = useVersionHistoryAnalytics();

	// certain browsers may not allow copying
	// only whiteboards support copying content
	const isCopyAllowed = isCanvasContentType(contentType) && eventService.hasEventHandler('copy');

	const handleCopyClick = useCallback(() => {
		eventService.triggerEvent('copy');
		if (selectedVersion) {
			onCopyVersionContent(selectedVersion);
		}
	}, [eventService, selectedVersion, onCopyVersionContent]);

	if (error && !loading) {
		return (
			<ErrorDisplay error={error}>
				<ErrorState refetch={refetch} />
			</ErrorDisplay>
		);
	}

	return (
		<>
			<Box xcss={bodyContentWrapperStyles} data-testId="version-history-content">
				<Stack>
					<SectionTitle title={i18n.currentVersionTitle} />
					<VersionHistoryCurrentVersionItem
						handleVersionClick={handleVersionClick}
						isSelected={!selectedVersion}
						title={title}
					/>
				</Stack>
				{autosavedVersions.length > 0 && (
					<Stack>
						<SectionTitle title={i18n.autoSavedVersionsTitle} />
						{autosavedVersions.map((version: VersionHistoryNodeType) => {
							return (
								<VersionHistoryItem
									version={version}
									key={version.number}
									handleVersionClick={handleVersionClick}
									isSelected={version.number === selectedVersion?.number}
									isCopyAllowed={isCopyAllowed}
									handleCopyClick={handleCopyClick}
								/>
							);
						})}
						{hasNextPage && (
							<ObservableSpinner
								cursor={cursor}
								testId="version-history-loading-spinner"
								onScrollTo={loadMoreVersions}
								observerOptions={{
									root: null,
									rootMargin: '0px',
									threshold: 0.05,
								}}
							/>
						)}
					</Stack>
				)}
			</Box>
			<Box paddingBlock="space.200" paddingInlineEnd="space.200" paddingInlineStart="space.300">
				<Text align="center" as="p" color="color.text.subtlest">
					<FormattedMessage {...contentTypeMessaging[contentType]} />
				</Text>
			</Box>
		</>
	);
};
